import React from 'react';
import {
  FaPlane,
  FaCheck,
  FaMapMarkerAlt,
  FaRegUser,
  FaRegClock,
  FaCommentDots,
  FaExclamationTriangle,
  FaShieldAlt,
  FaUndo,
  FaTimesCircle,
  FaBoxOpen,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';
import Icon from '../ui/Icon';
import { Stagger, StaggerItem } from '../motion';
import type {
  JourneyResponse,
  TrackingEventDto,
} from '../../types/tracking';
import {
  iconForName,
  fmtTime,
  shortDate,
  fmtRelativeTime,
  prettyVendorStatus,
  prettyEventLabel,
} from './tracking-utils';
import styles from './JourneyTimeline.module.css';

/* ============================================================
   Tone system — mirrors the internal timeline. The colour alone
   signals status: green = done / good, amber = carrier / warning,
   red = problem, muted = upcoming, primary highlight = latest.
   ============================================================ */
type Tone = 'green' | 'amber' | 'rose' | 'muted' | 'primary';

type UnifiedRow =
  | { kind: 'event'; event: TrackingEventDto }
  | { kind: 'vendor'; event: TrackingEventDto }
  | { kind: 'ops'; event: TrackingEventDto }
  | { kind: 'outcome'; event: TrackingEventDto };

/**
 * Icon + tone for generic lifecycle events (everything that isn't an ops
 * update, carrier update, or box outcome). The TEXT comes straight from the
 * backend `event.label`, so the public log reads exactly like the internal
 * timeline — this map only styles the node. Icon names resolve via
 * `iconForName`. Unknown types fall back to a neutral clock.
 */
const EVENT_NODE_META: Record<string, { icon: string; tone: Tone }> = {
  BOOKING_CREATED: { icon: 'Package', tone: 'green' },
  BOOKING_CONFIRMED: { icon: 'CheckCircle2', tone: 'green' },
  BOOKING_ACCEPTED: { icon: 'CheckCircle2', tone: 'green' },
  BOOKING_BAGGED: { icon: 'Layers', tone: 'green' },
  PICKUP_SCANNED: { icon: 'Package', tone: 'green' },
  PICKED_UP: { icon: 'Package', tone: 'green' },
  BRANCH_IN: { icon: 'Building2', tone: 'green' },
  BRANCH_OUT: { icon: 'Building2', tone: 'green' },
  PACKED_STAGE1: { icon: 'Package', tone: 'green' },
  PACKED_STAGE2: { icon: 'Package', tone: 'green' },
  INTO_BAG: { icon: 'Layers', tone: 'green' },
  BAG_BOX_ADDED: { icon: 'Layers', tone: 'green' },
  BAG_SEALED: { icon: 'Layers', tone: 'green' },
  BAG_RECEIVED: { icon: 'Building2', tone: 'green' },
  BAG_IN_TRANSIT: { icon: 'Truck', tone: 'green' },
  BAG_LOADED_ON_TRIP: { icon: 'Truck', tone: 'green' },
  LOADED_ON_TRIP: { icon: 'Truck', tone: 'green' },
  INTO_MAWB: { icon: 'Plane', tone: 'green' },
  BAG_INTO_MAWB: { icon: 'Plane', tone: 'green' },
  MAWB_CREATED: { icon: 'Plane', tone: 'green' },
  MAWB_PENDING_APPROVAL: { icon: 'Clock', tone: 'green' },
  MAWB_SEALED: { icon: 'Layers', tone: 'green' },
  MAWB_XRAY_CLEARED: { icon: 'CheckCircle2', tone: 'green' },
  MAWB_ASSIGNED_FOR_DELIVERY: { icon: 'Truck', tone: 'green' },
  MAWB_DISPATCHED: { icon: 'Navigation', tone: 'green' },
  MAWB_HANDED_TO_CARRIER: { icon: 'Plane', tone: 'green' },
  MAWB_DEPARTED: { icon: 'Plane', tone: 'green' },
  MAWB_ARRIVED_DESTINATION: { icon: 'MapPin', tone: 'green' },
  MAWB_RECEIVED_AT_BRANCH: { icon: 'Building2', tone: 'green' },
  MAWB_DELIVERED_TO_AGENT: { icon: 'CheckCircle2', tone: 'green' },
  TRIP_DISPATCHED: { icon: 'Truck', tone: 'green' },
  TRIP_ARRIVED: { icon: 'MapPin', tone: 'green' },
  TRIP_DELIVERED: { icon: 'CheckCircle2', tone: 'green' },
  OUT_FOR_DELIVERY: { icon: 'Send', tone: 'amber' },
  DELIVERED: { icon: 'CheckCircle2', tone: 'green' },
  HELD_BY_CUSTOMS: { icon: 'ShieldAlert', tone: 'amber' },
};

/**
 * Customer-friendly wording for the "All updates" log — replaces warehouse
 * terms (Into bag / Bag sealed / Loaded on trip / MAWB departed) with plain
 * language a customer understands. Falls back to the backend label.
 */
const PUBLIC_EVENT_LABELS: Record<string, string> = {
  BOOKING_CREATED: 'Shipment booked',
  BOOKING_CONFIRMED: 'Booking confirmed',
  PICKUP_SCANNED: 'Pickup scheduled',
  PICKED_UP: 'Picked up from sender',
  BRANCH_IN: 'Arrived at facility',
  PACKED_STAGE1: 'Being packed',
  PACKED_STAGE2: 'Packed',
  INTO_BAG: 'Packed',
  BAG_BOX_ADDED: 'Packed',
  BAG_SEALED: 'Packed & sealed',
  BAG_RECEIVED: 'Received at facility',
  BAG_IN_TRANSIT: 'In transit',
  BAG_LOADED_ON_TRIP: 'In transit',
  LOADED_ON_TRIP: 'In transit',
  INTO_MAWB: 'Prepared for export',
  BAG_INTO_MAWB: 'Prepared for export',
  MAWB_CREATED: 'Prepared for export',
  MAWB_SEALED: 'Prepared for export',
  MAWB_XRAY_CLEARED: 'Cleared for export',
  MAWB_ASSIGNED_FOR_DELIVERY: 'Handed to the airline',
  MAWB_DISPATCHED: 'In transit',
  MAWB_HANDED_TO_CARRIER: 'Handed to the airline',
  MAWB_DEPARTED: 'Departed',
  MAWB_ARRIVED_DESTINATION: 'Arrived at destination',
  MAWB_RECEIVED_AT_BRANCH: 'Arrived at destination facility',
  MAWB_DELIVERED_TO_AGENT: 'Delivered',
  TRIP_DISPATCHED: 'In transit',
  TRIP_ARRIVED: 'Arrived at destination',
  TRIP_DELIVERED: 'Delivered',
  OUT_FOR_DELIVERY: 'Out for delivery',
  DELIVERED: 'Delivered',
  HELD_BY_CUSTOMS: 'Held at customs',
};

function friendlyEventLabel(event: TrackingEventDto): string {
  return (
    PUBLIC_EVENT_LABELS[String(event.eventType)] ??
    prettyEventLabel(event.label)
  );
}

/** Ops-status-kind → icon + tone + label. */
const OPS_KIND_META: Record<
  string,
  { icon: IconType; tone: Tone; label: string }
> = {
  ON_TRACK: { icon: FaCheck, tone: 'green', label: 'On track' },
  DELAYED: { icon: FaRegClock, tone: 'amber', label: 'Delayed' },
  HELD: { icon: FaShieldAlt, tone: 'amber', label: 'Held' },
  ATTEMPTED: { icon: FaExclamationTriangle, tone: 'amber', label: 'Attempted' },
  RESCHEDULED: { icon: FaRegClock, tone: 'amber', label: 'Rescheduled' },
  EXCEPTION: { icon: FaExclamationTriangle, tone: 'rose', label: 'Exception' },
  DAMAGED: { icon: FaShieldAlt, tone: 'rose', label: 'Damaged' },
  LOST: { icon: FaTimesCircle, tone: 'rose', label: 'Lost' },
  INFO: { icon: FaCommentDots, tone: 'muted', label: 'Info' },
};

/** Manually-recorded box outcome events that aren't pipeline stages. */
const OUTCOME_EVENT_META: Record<
  string,
  { icon: IconType; tone: Tone; label: string }
> = {
  DELIVERY_ATTEMPTED: {
    icon: FaExclamationTriangle,
    tone: 'amber',
    label: 'Delivery attempted',
  },
  HELD_BY_CUSTOMS: {
    icon: FaShieldAlt,
    tone: 'amber',
    label: 'Held by customs',
  },
  RTO_INITIATED: { icon: FaUndo, tone: 'amber', label: 'Return to origin' },
  EXCEPTION: { icon: FaExclamationTriangle, tone: 'rose', label: 'Exception' },
  DAMAGED: { icon: FaShieldAlt, tone: 'rose', label: 'Damaged' },
  LOST: { icon: FaTimesCircle, tone: 'rose', label: 'Lost' },
  RTO_DELIVERED: { icon: FaBoxOpen, tone: 'green', label: 'RTO delivered' },
};

const TONE_CLASS: Record<Tone, string> = {
  green: styles.toneGreen,
  amber: styles.toneAmber,
  rose: styles.toneRose,
  muted: styles.toneMuted,
  primary: styles.tonePrimary,
};

interface Props {
  journey: JourneyResponse;
  /** Hide the card header (identifier/status) — used inside the "All updates"
   * collapsible where the milestone hero already shows those. */
  hideHeader?: boolean;
}

/**
 * The vertical journey timeline — a CSS-Modules / react-icons port of the
 * internal TMS `journey-timeline.tsx`. ONE chronological list (newest first)
 * of every event the backend deems public, rendered with the SAME exact
 * `event.label` text the internal module shows. The backend filters by the
 * org's per-type Tracking Visibility settings and sorts newest-first; we
 * REVERSE to oldest-first so the customer reads the journey top→bottom and
 * the latest status lands at the bottom. Ops/carrier/outcome rows keep their
 * richer styling.
 */
const JourneyTimeline: React.FC<Props> = ({ journey, hideHeader = false }) => {
  // Oldest-first: journey flows down the page, current status at the bottom.
  const events = [...(journey.events ?? [])].reverse();

  // One row per event. Ops/carrier/outcome get their dedicated rows (notes +
  // ETA); every other lifecycle event renders generically off `event.label`.
  const renderRows: UnifiedRow[] = events.map((event) => {
    const t = String(event.eventType);
    if (t === 'MAWB_VENDOR_STATUS_UPDATE')
      return { kind: 'vendor' as const, event };
    if (t === 'OPS_STATUS_UPDATE') return { kind: 'ops' as const, event };
    if (OUTCOME_EVENT_META[t]) return { kind: 'outcome' as const, event };
    return { kind: 'event' as const, event };
  });

  const carrierCount = events.filter(
    (e) => e.eventType === 'MAWB_VENDOR_STATUS_UPDATE',
  ).length;

  // Meaningful, shipment-specific header — the tracking number is the
  // title, with the current status + last-updated beneath it.
  const headerStatus = [
    journey.current?.statusLabel ?? journey.current?.status ?? null,
    journey.lastUpdatedAt
      ? `Updated ${fmtRelativeTime(journey.lastUpdatedAt)}`
      : null,
  ]
    .filter(Boolean)
    .join('  ·  ');

  return (
    <div className={styles.card}>
      {!hideHeader && (
        <div className={styles.header}>
          <span className={styles.headerIcon} aria-hidden="true">
            <Icon icon={FaBoxOpen} />
          </span>
          <div className={styles.headerText}>
            <p className={styles.headerEyebrow}>Tracking</p>
            <p className={styles.headerTitle}>{journey.identifier}</p>
            {headerStatus && (
              <p className={styles.headerSub}>{headerStatus}</p>
            )}
          </div>
          {carrierCount > 0 && (
            <span className={styles.carrierTag}>
              <Icon icon={FaPlane} />
              {carrierCount} carrier
            </span>
          )}
        </div>
      )}

      <div className={styles.body}>
        {renderRows.length === 0 ? (
          <div className={styles.emptyRail}>
            <Icon icon={FaRegClock} />
            <p>No activity recorded yet.</p>
          </div>
        ) : (
          <Stagger as="ul" className={styles.list} stagger={0.07} amount={0.05}>
            {renderRows.map((row, i) => {
              const isFirst = i === 0;
              const isLast = i === renderRows.length - 1;
              // Oldest-first: the LAST (bottom) row is the latest. Every event
              // in the log already happened, so all connectors render filled.
              const isLatest = i === renderRows.length - 1;
              const common = {
                isFirst,
                isLast,
                isLatest,
                topReached: !isFirst,
                bottomReached: !isLast,
              };

              if (row.kind === 'vendor') {
                return (
                  <VendorRow
                    key={row.event.id}
                    event={row.event}
                    {...common}
                  />
                );
              }
              if (row.kind === 'ops') {
                return (
                  <OpsRow key={row.event.id} event={row.event} {...common} />
                );
              }
              if (row.kind === 'outcome') {
                return (
                  <OutcomeRow
                    key={row.event.id}
                    event={row.event}
                    {...common}
                  />
                );
              }
              return (
                <EventRow key={row.event.id} event={row.event} {...common} />
              );
            })}
          </Stagger>
        )}
      </div>
    </div>
  );
};

export default JourneyTimeline;

/* ============================================================
   Shared timeline node + connector column.
   ============================================================ */
interface NodeProps {
  icon: IconType;
  tone: Tone;
  /** "tick" for something that happened, null for an upcoming stage. */
  corner: 'tick' | null;
  isFirst: boolean;
  isLast: boolean;
  isLatest: boolean;
  topReached: boolean;
  bottomReached: boolean;
  title?: string;
}

const TimelineNode: React.FC<NodeProps> = ({
  icon,
  tone,
  corner,
  isFirst,
  isLast,
  isLatest,
  topReached,
  bottomReached,
  title,
}) => (
  <div className={styles.nodeCol}>
    {!isFirst && (
      <span
        className={`${styles.railTop} ${
          topReached ? styles.railReached : ''
        }`}
        aria-hidden="true"
      />
    )}
    {!isLast && (
      <span
        className={`${styles.railBottom} ${
          bottomReached ? styles.railReached : ''
        }`}
        aria-hidden="true"
      />
    )}

    <div className={styles.nodeWrap}>
      {isLatest && (
        <span
          className={`${styles.nodeHalo} ${TONE_CLASS[tone]}`}
          aria-hidden="true"
        />
      )}
      <div
        className={`${styles.nodeCircle} ${TONE_CLASS[tone]} ${
          isLatest ? styles.nodeLatest : ''
        }`}
        title={title}
      >
        <Icon icon={icon} />
      </div>
      {corner === 'tick' && (
        <span
          className={`${styles.nodeTick} ${TONE_CLASS[tone]}`}
          aria-hidden="true"
        >
          <Icon icon={FaCheck} />
        </span>
      )}
    </div>
  </div>
);

/* ── Badges ── */
const LatestBadge: React.FC<{ tone: Tone }> = ({ tone }) => (
  <span className={`${styles.badge} ${styles.badgeLatest} ${TONE_CLASS[tone]}`}>
    <span className={styles.badgePulse} aria-hidden="true" />
    Latest
  </span>
);

const Subline: React.FC<{
  ts?: string | null;
  branch?: string | null;
  actor?: string | null;
  showRelative?: boolean;
}> = ({ ts, branch, actor, showRelative }) => {
  const hasTime = !!ts;
  if (!hasTime && !branch && !actor) return null;
  return (
    <div className={styles.subline}>
      {hasTime && (
        <span className={styles.sublineTime}>
          {shortDate(ts)} <span className={styles.dot}>·</span> {fmtTime(ts)}
          {showRelative && (
            <span className={styles.sublineRel}> ({fmtRelativeTime(ts)})</span>
          )}
        </span>
      )}
      {branch && (
        <span className={styles.sublineMeta}>
          <Icon icon={FaMapMarkerAlt} />
          {branch}
        </span>
      )}
      {actor && (
        <span className={styles.sublineMeta}>
          <Icon icon={FaRegUser} />
          {actor}
        </span>
      )}
    </div>
  );
};

/* ============================================================
   Row variants
   ============================================================ */
type RowCommon = {
  isFirst: boolean;
  isLast: boolean;
  isLatest: boolean;
  topReached: boolean;
  bottomReached: boolean;
};

const EventRow: React.FC<RowCommon & { event: TrackingEventDto }> = ({
  event,
  isLatest,
  ...rest
}) => {
  const meta = EVENT_NODE_META[String(event.eventType)] ?? {
    icon: 'Clock',
    tone: 'green' as Tone,
  };
  const ts = event.recordedAt ?? event.scannedAt;
  // Carrier handler on a "Handed to carrier" event — the external party the
  // cargo was handed to (not internal staff), so it's fine to show.
  const handler =
    String(event.eventType) === 'MAWB_HANDED_TO_CARRIER'
      ? (((event.metadata as Record<string, unknown> | null)
          ?.receivedByName as string | undefined) ?? undefined)
      : undefined;
  const label = friendlyEventLabel(event);
  return (
    <StaggerItem as="li" direction="up" className={styles.row}>
      <TimelineNode
        icon={iconForName(meta.icon)}
        tone={meta.tone}
        corner="tick"
        isLatest={isLatest}
        title={label}
        {...rest}
      />
      <div className={styles.rowBody}>
        <div className={styles.rowHead}>
          <p
            className={`${styles.rowLabel} ${
              isLatest ? styles.rowLabelLatest : ''
            }`}
          >
            {label}
          </p>
          {isLatest && <LatestBadge tone="primary" />}
        </div>
        {event.note && <p className={styles.rowNote}>“{event.note}”</p>}
        {/* Customer-facing: date/time only — never internal staff names. */}
        <Subline ts={ts} actor={handler} />
      </div>
    </StaggerItem>
  );
};

const VendorRow: React.FC<RowCommon & { event: TrackingEventDto }> = ({
  event,
  isLatest,
  ...rest
}) => {
  const statusType = (event.metadata as Record<string, unknown> | null)
    ?.vendorStatusType;
  const ts = event.recordedAt ?? event.scannedAt;

  return (
    <StaggerItem as="li" direction="up" className={styles.row}>
      <TimelineNode
        icon={FaPlane}
        tone="amber"
        corner="tick"
        isLatest={isLatest}
        title="Carrier-reported update"
        {...rest}
      />
      <div className={styles.rowBody}>
        <div className={styles.rowHead}>
          <p className={`${styles.rowLabel} ${styles.rowLabelAmber}`}>
            Carrier · {prettyVendorStatus(statusType)}
          </p>
          {isLatest && <LatestBadge tone="amber" />}
        </div>
        {event.note && <p className={styles.rowNote}>“{event.note}”</p>}
        {/* Customer-facing: date/time only — no internal staff name. */}
        <Subline ts={ts} showRelative />
      </div>
    </StaggerItem>
  );
};

const OpsRow: React.FC<RowCommon & { event: TrackingEventDto }> = ({
  event,
  isLatest,
  ...rest
}) => {
  const md = (event.metadata as Record<string, unknown> | null) ?? {};
  const rawStatus =
    typeof md.statusKind === 'string' && md.statusKind.trim()
      ? md.statusKind.trim()
      : 'Update';
  const meta =
    OPS_KIND_META[rawStatus] ??
    ({ icon: FaCommentDots, tone: 'amber' as Tone, label: rawStatus });
  const reason = typeof md.reason === 'string' ? md.reason : null;
  const eta =
    typeof md.expectedDeliveryAt === 'string' ? md.expectedDeliveryAt : null;
  const ts = event.recordedAt ?? event.scannedAt;

  return (
    <StaggerItem as="li" direction="up" className={styles.row}>
      <TimelineNode
        icon={meta.icon}
        tone={meta.tone}
        corner="tick"
        isLatest={isLatest}
        title={`Ops · ${meta.label}`}
        {...rest}
      />
      <div className={styles.rowBody}>
        <div className={styles.rowHead}>
          <p className={`${styles.rowLabel} ${TONE_CLASS[meta.tone]}`}>
            {meta.label}
          </p>
          {isLatest && <LatestBadge tone={meta.tone} />}
        </div>
        {reason && <p className={styles.rowReason}>{reason}</p>}
        {event.note && <p className={styles.rowNote}>“{event.note}”</p>}
        {/* Customer-facing: date/time only — no internal staff name. */}
        <Subline ts={ts} showRelative />
        {eta && (
          <p className={styles.rowEta}>
            <Icon icon={FaRegClock} />
            ETA{' '}
            <strong>
              {new Date(eta).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })}
            </strong>
          </p>
        )}
      </div>
    </StaggerItem>
  );
};

const OutcomeRow: React.FC<RowCommon & { event: TrackingEventDto }> = ({
  event,
  isLatest,
  ...rest
}) => {
  const meta =
    OUTCOME_EVENT_META[String(event.eventType)] ??
    ({
      icon: FaCommentDots,
      tone: 'muted' as Tone,
      label: prettyEventLabel(event.eventType),
    });
  const ts = event.recordedAt ?? event.scannedAt;

  return (
    <StaggerItem as="li" direction="up" className={styles.row}>
      <TimelineNode
        icon={meta.icon}
        tone={meta.tone}
        corner="tick"
        isLatest={isLatest}
        title={`Recorded · ${meta.label}`}
        {...rest}
      />
      <div className={styles.rowBody}>
        <div className={styles.rowHead}>
          <p className={`${styles.rowLabel} ${TONE_CLASS[meta.tone]}`}>
            {meta.label}
          </p>
          {isLatest ? (
            <LatestBadge tone={meta.tone} />
          ) : (
            <span className={styles.badgeUpcoming}>Recorded</span>
          )}
        </div>
        {event.note && <p className={styles.rowNote}>“{event.note}”</p>}
        {/* Customer-facing: date/time only — no internal staff name. */}
        <Subline ts={ts} showRelative />
      </div>
    </StaggerItem>
  );
};
